import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type AdminCrumb = { label: string; href?: string };

type AdminChromeContextValue = {
  crumbs: AdminCrumb[];
  setCrumbs: (crumbs: AdminCrumb[]) => void;
  clearCrumbs: () => void;
  pageAction: React.ReactNode | null;
  setPageAction: (node: React.ReactNode | null) => void;
};

const AdminChromeContext = createContext<AdminChromeContextValue | null>(null);

export const AdminChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [crumbs, setCrumbsState] = useState<AdminCrumb[]>([]);
  const [pageAction, setPageAction] = useState<React.ReactNode | null>(null);

  const setCrumbs = useCallback((next: AdminCrumb[]) => setCrumbsState(next), []);
  const clearCrumbs = useCallback(() => setCrumbsState([]), []);

  const value = useMemo(
    () => ({ crumbs, setCrumbs, clearCrumbs, pageAction, setPageAction }),
    [crumbs, setCrumbs, clearCrumbs, pageAction]
  );

  return <AdminChromeContext.Provider value={value}>{children}</AdminChromeContext.Provider>;
};

export function useAdminChrome() {
  const ctx = useContext(AdminChromeContext);
  if (!ctx) {
    return {
      crumbs: [] as AdminCrumb[],
      setCrumbs: (_: AdminCrumb[]) => undefined,
      clearCrumbs: () => undefined,
      pageAction: null as React.ReactNode | null,
      setPageAction: (_: React.ReactNode | null) => undefined,
    };
  }
  return ctx;
}

/** Set breadcrumbs for nested admin pages; clears on unmount. */
export function useAdminBreadcrumbs(crumbs: AdminCrumb[]) {
  const { setCrumbs, clearCrumbs } = useAdminChrome();
  React.useEffect(() => {
    setCrumbs(crumbs);
    return () => clearCrumbs();
  }, [setCrumbs, clearCrumbs, JSON.stringify(crumbs)]);
}
